import { Node, Edge } from 'reactflow';
// Import PlaybookNodeData from the store where it's defined
import { PlaybookNodeData } from '../store/usePlaybookStore';

// Remove duplicate interface definition
// interface PlaybookNodeData { ... }

interface ConversionResult {
  nodes: Node<PlaybookNodeData>[];
  edges: Edge[];
}

/**
 * Converts a parsed playbook object (from YAML) into React Flow nodes and edges.
 * @param playbook The parsed playbook object.
 * @returns An object containing arrays of nodes and edges.
 */
export const convertPlaybookToGraph = (playbook: any): ConversionResult => {
  if (!playbook || typeof playbook !== 'object') {
    console.error("Invalid playbook object provided for conversion.");
    return { nodes: [], edges: [] };
  }

  const nodes: Node<PlaybookNodeData>[] = [];
  const edges: Edge[] = [];
  let yPos = 50;
  const xPosRoot = 100;
  const xPosConfig = 400;
  const xPosDetails = 700; // For mapping/enrichment/filter nodes

  // --- Root Node ---
  const rootNodeId = 'playbook-root';
  nodes.push({
    id: rootNodeId,
    type: 'playbook-root', // Use the type defined in playbookNodeTypes
    position: { x: xPosRoot, y: yPos },
    data: { label: 'Playbook', version: playbook.version || 'N/A' },
  });
  yPos += 100;

  // --- Global Config Node ---
  if (playbook.global) {
    const globalNodeId = 'global-config';
    nodes.push({
      id: globalNodeId,
      type: 'global-config',
      position: { x: xPosConfig, y: yPos },
      data: {
        label: 'Global Config',
        config: playbook.global,
        filterCount: playbook.global.filters?.length || 0,
        mappingCount: playbook.global.mappings?.length || 0,
        enrichmentCount: playbook.global.enrichments?.length || 0,
      },
    });
    edges.push({
      id: `edge-${rootNodeId}-${globalNodeId}`,
      source: rootNodeId,
      target: globalNodeId,
      type: 'smoothstep', // Or 'custom' if using CustomEdge
    });

    // Add detail nodes for global config (optional visualization)
    let detailYPos = yPos - 40; // Start slightly above global node
    if (playbook.global.filters?.length > 0) {
      const filtersNodeId = 'global-filters';
        nodes.push({
          id: filtersNodeId, type: 'filter', position: { x: xPosDetails, y: detailYPos },
          // Store the actual filters array
          data: { label: `Global Filters (${playbook.global.filters.length})`, filters: playbook.global.filters }
        });
        edges.push({ id: `edge-${globalNodeId}-${filtersNodeId}`, source: globalNodeId, target: filtersNodeId, type: 'smoothstep' });
      detailYPos += 80;
    }
    if (playbook.global.mappings?.length > 0) {
      const mappingsNodeId = 'global-mappings';
        nodes.push({
          id: mappingsNodeId, type: 'mapping', position: { x: xPosDetails, y: detailYPos },
          // Store the actual mappings array
          data: { label: `Global Mappings (${playbook.global.mappings.length})`, mappings: playbook.global.mappings }
        });
        edges.push({ id: `edge-${globalNodeId}-${mappingsNodeId}`, source: globalNodeId, target: mappingsNodeId, type: 'smoothstep' });
      detailYPos += 80;
    }
     if (playbook.global.enrichments?.length > 0) {
      const enrichmentsNodeId = 'global-enrichments';
        nodes.push({
          id: enrichmentsNodeId, type: 'enrichment', position: { x: xPosDetails, y: detailYPos },
          // Store the actual enrichments array
          data: { label: `Global Enrichments (${playbook.global.enrichments.length})`, enrichments: playbook.global.enrichments }
        });
        edges.push({ id: `edge-${globalNodeId}-${enrichmentsNodeId}`, source: globalNodeId, target: enrichmentsNodeId, type: 'smoothstep' });
      detailYPos += 80;
    }

    yPos = Math.max(yPos + 100, detailYPos); // Adjust yPos for next section
  }

  // --- Event Specific Node (Switch) ---
  if (playbook.eventSpecific && Object.keys(playbook.eventSpecific).length > 0) {
    const eventSwitchNodeId = 'event-switch';
    nodes.push({
      id: eventSwitchNodeId,
      type: 'event-switch',
      position: { x: xPosConfig, y: yPos },
      data: { label: 'Event Specific', config: playbook.eventSpecific },
    });
    edges.push({
      id: `edge-${rootNodeId}-${eventSwitchNodeId}`,
      source: rootNodeId,
      target: eventSwitchNodeId,
      type: 'smoothstep',
    });

    // Add detail nodes for each event's config
    let eventDetailYPos = yPos - 40; // Start position for event details
    Object.entries(playbook.eventSpecific).forEach(([eventName, eventConfig]: [string, any]) => {
      const eventHandleId = `event-${eventName}-output`; // Matches handle ID in EventSwitchNode
      let currentEventY = eventDetailYPos;

      if (eventConfig.filters?.length > 0) {
        const eventFiltersNodeId = `event-${eventName}-filters`;
        nodes.push({
          id: eventFiltersNodeId, type: 'filter', position: { x: xPosDetails, y: currentEventY },
          // Store the actual event-specific filters array
          data: { label: `${eventName} Filters (${eventConfig.filters.length})`, filters: eventConfig.filters, eventName: eventName }
        });
        edges.push({ id: `edge-${eventSwitchNodeId}-${eventFiltersNodeId}`, source: eventSwitchNodeId, sourceHandle: eventHandleId, target: eventFiltersNodeId, type: 'smoothstep' });
        currentEventY += 80;
      }
       if (eventConfig.mappings?.length > 0) {
        const eventMappingsNodeId = `event-${eventName}-mappings`;
        nodes.push({
          id: eventMappingsNodeId, type: 'mapping', position: { x: xPosDetails, y: currentEventY },
          // Store the actual event-specific mappings array
          data: { label: `${eventName} Mappings (${eventConfig.mappings.length})`, mappings: eventConfig.mappings, eventName: eventName }
        });
        edges.push({ id: `edge-${eventSwitchNodeId}-${eventMappingsNodeId}`, source: eventSwitchNodeId, sourceHandle: eventHandleId, target: eventMappingsNodeId, type: 'smoothstep' });
        currentEventY += 80;
      }
      if (eventConfig.enrichments?.length > 0) {
        const eventEnrichmentsNodeId = `event-${eventName}-enrichments`;
        nodes.push({
          id: eventEnrichmentsNodeId, type: 'enrichment', position: { x: xPosDetails, y: currentEventY },
          // Store the actual event-specific enrichments array
          data: { label: `${eventName} Enrichments (${eventConfig.enrichments.length})`, enrichments: eventConfig.enrichments, eventName: eventName }
        });
        edges.push({ id: `edge-${eventSwitchNodeId}-${eventEnrichmentsNodeId}`, source: eventSwitchNodeId, sourceHandle: eventHandleId, target: eventEnrichmentsNodeId, type: 'smoothstep' });
        currentEventY += 80;
      }
      eventDetailYPos = Math.max(eventDetailYPos + 80, currentEventY); // Ensure spacing between event groups
    });
  }

  // TODO: Add logic for pipeline YAML structure conversion

  console.log("Generated Nodes:", nodes);
  console.log("Generated Edges:", edges);

  return { nodes, edges };
};

/**
 * Converts React Flow nodes and edges back into a playbook object structure.
 * (Placeholder implementation)
 * @param nodes Array of React Flow nodes.
 * @param edges Array of React Flow edges.
 * @returns The reconstructed playbook object.
 */
export const convertGraphToPlaybook = (nodes: Node<PlaybookNodeData>[], edges: Edge[]): any => {
  const playbook: any = {
    global: { filters: [], mappings: [], enrichments: [], expressions: [], spreadings: [] }, // Initialize with all possible keys
    eventSpecific: {},
    default: { mappings: [], enrichments: [], expressions: [], spreadings: [] }, // Assuming default might exist or be added later
    params: [], // Assuming params might exist or be added later
    version: 'unknown',
  };

  // --- Root Node ---
  const rootNode = nodes.find(n => n.type === 'playbook-root');
  if (rootNode?.data?.version) {
    playbook.version = rootNode.data.version;
  } else {
     console.warn("Root node or version not found in graph.");
  }
  // TODO: Retrieve author if stored

  // --- Global Config ---
  const globalConfigNode = nodes.find(n => n.id === 'global-config');
  if (globalConfigNode) {
    const globalEdges = edges.filter(e => e.source === globalConfigNode.id);
    globalEdges.forEach(edge => {
      const targetNode = nodes.find(n => n.id === edge.target);
      if (targetNode) {
        switch (targetNode.type) {
          case 'filter':
            playbook.global.filters = targetNode.data?.filters || [];
            break;
          case 'mapping':
            playbook.global.mappings = targetNode.data?.mappings || [];
            break;
          case 'enrichment':
            playbook.global.enrichments = targetNode.data?.enrichments || [];
            break;
          // Add cases for expressions, spreadings if they get nodes
        }
      }
    });
    // Preserve any other global properties not represented by nodes (if needed)
    // This simple reconstruction assumes detail nodes hold the complete arrays.
    // A more robust approach might merge with original config stored in globalConfigNode.data.config
  } else {
     console.warn("Global config node not found in graph.");
  }


  // --- Event Specific ---
  const eventSwitchNode = nodes.find(n => n.id === 'event-switch');
  if (eventSwitchNode) {
    const eventEdges = edges.filter(e => e.source === eventSwitchNode.id);
    eventEdges.forEach(edge => {
      const targetNode = nodes.find(n => n.id === edge.target);
      if (targetNode?.data?.eventName) {
        const eventName = targetNode.data.eventName;
        // Ensure the event object exists
        if (!playbook.eventSpecific[eventName]) {
          playbook.eventSpecific[eventName] = { filters: [], mappings: [], enrichments: [], expressions: [], spreadings: [] };
        }

        switch (targetNode.type) {
          case 'filter':
            playbook.eventSpecific[eventName].filters = targetNode.data?.filters || [];
            break;
          case 'mapping':
            playbook.eventSpecific[eventName].mappings = targetNode.data?.mappings || [];
            break;
          case 'enrichment':
            playbook.eventSpecific[eventName].enrichments = targetNode.data?.enrichments || [];
            break;
           // Add cases for expressions, spreadings if they get nodes
        }
      }
    });
  } else {
    console.warn("Event switch node not found in graph.");
  }

  // TODO: Add logic for pipeline structure conversion (if applicable)
  // TODO: Handle 'default' section if represented in the graph
  // TODO: Handle 'params' section if represented in the graph

  // Clean up empty arrays if necessary (optional, depends on desired output)
  // e.g., if playbook.global.filters is empty, delete playbook.global.filters

  console.log("Reconstructed Playbook:", playbook);
  return playbook;
};
